import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, X } from "lucide-react";

export const AMetric = ({ label, value, sub, tone = "blue" }) => {
  const tones = { blue: "text-blue-300", purple: "text-purple-300", green: "text-emerald-300", amber: "text-amber-300", red: "text-red-300" };
  return (
    <div className="cc-card rounded-xl p-4" data-testid={`metric-${String(label).toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`cc-heading text-2xl font-700 mt-1 text-white`}>{value}</div>
      {sub != null && <div className={`text-xs mt-0.5 ${tones[tone]}`}>{sub}</div>}
    </div>
  );
};

export const ABadge = ({ children, tone = "default" }) => {
  const tones = {
    default: "border-white/10 bg-white/[0.04] text-slate-300",
    active: "border-emerald-400/30 bg-emerald-500/10 text-emerald-200",
    green: "border-emerald-400/30 bg-emerald-500/10 text-emerald-200",
    suspended: "border-red-400/30 bg-red-500/10 text-red-200",
    red: "border-red-400/30 bg-red-500/10 text-red-200",
    amber: "border-amber-400/30 bg-amber-500/10 text-amber-200",
    blue: "border-blue-400/30 bg-blue-500/10 text-blue-200",
    purple: "border-purple-400/30 bg-purple-500/10 text-purple-200",
  };
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] capitalize ${tones[tone] || tones.default}`}>{children}</span>;
};

export const ATable = ({ columns, rows, loading, empty = "No records found.", onRow }) => (
  <div className="cc-card rounded-xl overflow-hidden">
    <div className="overflow-x-auto cc-scroll">
      <table className="w-full text-sm min-w-[640px]">
        <thead>
          <tr className="border-b border-white/[0.06] text-left">
            {columns.map((c) => <th key={c.key} className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-500 whitespace-nowrap">{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>}
          {!loading && rows.length === 0 && <tr><td colSpan={columns.length} className="px-4 py-12 text-center text-slate-500">{empty}</td></tr>}
          {!loading && rows.map((r, i) => (
            <tr key={r.id || i} onClick={() => onRow && onRow(r)}
              className={`border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors ${onRow ? "cursor-pointer" : ""}`}>
              {columns.map((c) => <td key={c.key} className="px-4 py-3 text-slate-300 whitespace-nowrap">{c.render ? c.render(r) : (r[c.key] ?? "—")}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export const APager = ({ page, pages, onPage }) => (
  <div className="flex items-center justify-between mt-3 text-sm">
    <span className="text-slate-500">Page {page} of {pages}</span>
    <div className="flex gap-2">
      <button disabled={page <= 1} onClick={() => onPage(page - 1)} className="rounded-lg border border-white/10 px-3 py-1.5 text-slate-300 disabled:opacity-40 cc-nav-item">Prev</button>
      <button disabled={page >= pages} onClick={() => onPage(page + 1)} className="rounded-lg border border-white/10 px-3 py-1.5 text-slate-300 disabled:opacity-40 cc-nav-item">Next</button>
    </div>
  </div>
);

export const AConfirm = ({ open, title, message, requireReason, confirmLabel = "Confirm", danger, onConfirm, onCancel }) => {
  const [reason, setReason] = useState("");
  if (!open) return null;
  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
        <motion.div className="absolute inset-0 bg-black/60" initial={{ opacity: 0 }} animate={{ opacity: 1 }} onClick={onCancel} />
        <motion.div initial={{ opacity: 0, y: 16, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
          className="relative cc-card rounded-2xl p-6 w-full max-w-md">
          <div className="flex items-start gap-3">
            {danger && <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-500/15 text-red-300"><AlertTriangle size={20} /></div>}
            <div className="flex-1">
              <h3 className="cc-heading text-lg font-500 text-white">{title}</h3>
              <p className="text-sm text-slate-400 mt-1">{message}</p>
            </div>
            <button onClick={onCancel} className="text-slate-500"><X size={18} /></button>
          </div>
          {requireReason && (
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Reason (required, logged in audit trail)"
              className="cc-input w-full rounded-xl px-3 py-2 text-sm mt-4" data-testid="confirm-reason" />
          )}
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={onCancel} className="rounded-xl border border-white/12 px-4 py-2 text-sm text-slate-300 cc-nav-item">Cancel</button>
            <button
              onClick={() => onConfirm(reason)}
              disabled={requireReason && reason.trim().length < 3}
              data-testid="confirm-action"
              className={`rounded-xl px-4 py-2 text-sm font-500 disabled:opacity-40 ${danger ? "bg-red-500 text-white hover:bg-red-600" : "cc-btn-primary text-white"}`}>
              {confirmLabel}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export const APageHead = ({ title, subtitle, action }) => (
  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
    <div>
      <h1 className="cc-heading text-2xl sm:text-3xl font-600 tracking-tight text-white">{title}</h1>
      {subtitle && <p className="text-slate-400 text-sm mt-1">{subtitle}</p>}
    </div>
    {action}
  </div>
);
